import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaCheckCircle } from 'react-icons/fa';
import api from '../api/axios';
import Navbar from './Navbar';
import planbLogo from '../assets/planbjobs-logo.png';

const EMAIL_OTP_LENGTH = 4;
const PHONE_OTP_LENGTH = 4; // matches OTP_LENGTH used by /otp/send + /otp/verify in Register.jsx

/* Shared look, taken from the job search page (purple / pink brand) */
const inputCls =
  'w-full border border-purple-100 rounded-xl px-3.5 py-3 text-sm text-[#1E1033] bg-white placeholder:text-gray-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-[#9333EA]/40 focus:border-[#9333EA] transition';

const primaryBtn =
  'w-full bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white py-3 rounded-xl font-semibold text-sm shadow-sm ' +
  'hover:opacity-90 transition disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9333EA]/50 focus-visible:ring-offset-2';

function ForgotPassword() {
  const [user] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [method, setMethod] = useState('email'); // 'email' | 'phone' — tab toggle
  const [step, setStep] = useState(1); // 1: contact info, 2: otp, 3: new password
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const otpLength = method === 'email' ? EMAIL_OTP_LENGTH : PHONE_OTP_LENGTH;
  const [otp, setOtp] = useState(Array(EMAIL_OTP_LENGTH).fill(''));
  const [otpToken, setOtpToken] = useState(''); // proves this contact was just verified
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const otpInputRefs = useRef([]);

  // Switching tabs resets the flow back to step 1 so you don't end up
  // verifying an OTP of the wrong length against the wrong contact method.
  const handleMethodChange = (m) => {
    setMethod(m);
    setStep(1);
    setOtp(Array(m === 'email' ? EMAIL_OTP_LENGTH : PHONE_OTP_LENGTH).fill(''));
    setOtpToken('');
    setError('');
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (method === 'email') {
        await api.post('/otp/email/send', { email });
      } else {
        await api.post('/otp/send', { phone, purpose: 'reset' });
      }
      setOtp(Array(otpLength).fill(''));
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < otpLength - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    const code = otp.slice(0, otpLength).join('');
    if (code.length !== otpLength) {
      setError(`Please enter the complete ${otpLength}-digit code.`);
      return;
    }
    setLoading(true);
    try {
      let token = code;
      if (method === 'email') {
        const res = await api.post('/otp/email/verify', { email, otp: code });
        token = res.data.otp_token || '';
        if (!token) {
          setError('Verification worked but no token came back. Please try again.');
          return;
        }
      } else {
        const res = await api.post('/otp/verify', { phone, otp: code });
        // same keys Register.jsx reads from this endpoint
        token = res.data.otp_token || res.data.token || '';
        if (!token) {
          setError('Verification worked but no token came back. Please try again.');
          return;
        }
      }
      setOtpToken(token);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      if (method === 'email') {
        await api.post('/reset-password', {
          email,
          otp: otpToken,
          password: newPassword,
          password_confirmation: confirmPassword,
        });
      } else {
        await api.post('/reset-password-phone', {
          phone,
          otp_token: otpToken,
          password: newPassword,
          password_confirmation: confirmPassword,
        });
      }
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  const contactValue = method === 'email' ? email : phone;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#FAF5FF] to-[#FDF2F8]">
      <Navbar user={user} />

      <main className="relative flex-1 flex items-center justify-center px-4 py-10 sm:py-14 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-[#7C3AED]/10 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-[#DB2777]/10 blur-3xl pointer-events-none" aria-hidden="true" />

        <div className="relative w-full max-w-md bg-white rounded-3xl border border-purple-100 shadow-[0_30px_70px_-30px_rgba(124,58,237,0.4)] p-6 sm:p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src={planbLogo} alt="PlanB Jobs" className="h-12 w-auto max-w-[200px] object-contain" />
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-6" role="list" aria-label="Progress">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2" role="listitem">
                <div
                  aria-current={step === s ? 'step' : undefined}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition ${
                    step > s
                      ? 'bg-[#10B981] text-white'
                      : step === s
                      ? 'bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white shadow-md shadow-[#7C3AED]/30'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {step > s ? <FaCheckCircle size={14} /> : s}
                </div>
                {s < 3 && <div className={`w-8 h-0.5 rounded ${step > s ? 'bg-[#10B981]' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>

          {/* Email / Phone tab toggle — only on step 1, switching mid-flow would invalidate an in-progress OTP */}
          {step === 1 && (
            <div className="flex bg-[#F5F0FA] rounded-xl p-1 mb-6" role="tablist">
              {[
                { key: 'email', label: 'Email' },
                { key: 'phone', label: 'Phone' },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={method === t.key}
                  onClick={() => handleMethodChange(t.key)}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9333EA]/50 ${
                    method === t.key ? 'bg-white text-[#7C3AED] shadow-sm' : 'text-gray-500 hover:text-[#7C3AED]'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {error && (
            <div role="alert" className="bg-red-50 text-red-600 border border-red-100 p-3 rounded-xl mb-4 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Email or Phone */}
          {step === 1 && (
            <form onSubmit={handleSendOtp}>
              <h1 className="text-2xl font-bold text-center text-[#1E1033] mb-1">Forgot password?</h1>
              <p className="text-center text-gray-500 text-sm mb-6">
                {method === 'email'
                  ? "Enter your email and we'll send you a 6-digit code"
                  : "Enter your phone number and we'll text you a 4-digit code"}
              </p>
              <div className="mb-6">
                <label htmlFor="fp-contact" className="block text-xs font-bold text-gray-500 uppercase mb-1.5">
                  {method === 'email' ? 'Email' : 'Phone number'}
                </label>
                {method === 'email' ? (
                  <input
                    id="fp-contact"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    className={inputCls}
                  />
                ) : (
                  <div className="flex items-center border border-purple-100 rounded-xl overflow-hidden bg-white focus-within:ring-2 focus-within:ring-[#9333EA]/40 focus-within:border-[#9333EA] transition">
                    <span className="px-3.5 py-3 text-sm text-gray-500 bg-[#FAF5FF] border-r border-purple-100">+91</span>
                    <input
                      id="fp-contact"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      required
                      placeholder="98765 43210"
                      className="flex-1 min-w-0 px-3.5 py-3 text-sm text-[#1E1033] outline-none placeholder:text-gray-400"
                    />
                  </div>
                )}
              </div>
              <button type="submit" disabled={loading} className={primaryBtn}>
                {loading ? 'Sending...' : 'Send OTP'}
              </button>
            </form>
          )}

          {/* Step 2: OTP */}
          {step === 2 && (
            <form onSubmit={handleVerifyOtp}>
              <h1 className="text-2xl font-bold text-center text-[#1E1033] mb-1">Enter code</h1>
              <p className="text-center text-gray-500 text-sm mb-6">
                We sent a {otpLength}-digit code to{' '}
                <span className="font-semibold text-[#1E1033] break-all">
                  {method === 'phone' ? `+91 ${contactValue}` : contactValue}
                </span>
              </p>
              <div className="flex justify-center gap-2.5 mb-6">
                {otp.slice(0, otpLength).map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpInputRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    aria-label={`Digit ${i + 1} of ${otpLength}`}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    autoFocus={i === 0}
                    className={`w-12 h-12 text-center text-lg font-bold rounded-xl border bg-white focus:outline-none focus:ring-2 focus:ring-[#9333EA]/40 focus:border-[#9333EA] transition ${
                      digit ? 'border-[#9333EA] text-[#7C3AED]' : 'border-purple-100 text-[#1E1033]'
                    }`}
                  />
                ))}
              </div>
              <button type="submit" disabled={loading} className={primaryBtn}>
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>
              <p className="text-center text-sm mt-4 text-gray-500">
                Didn't get the code?{' '}
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={loading}
                  className="text-[#7C3AED] font-semibold hover:underline disabled:opacity-50"
                >
                  Resend
                </button>
              </p>
            </form>
          )}

          {/* Step 3: New password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword}>
              <h1 className="text-2xl font-bold text-center text-[#1E1033] mb-1">Set new password</h1>
              <p className="text-center text-gray-500 text-sm mb-6">Choose a strong password for your account</p>

              <div className="mb-4">
                <label htmlFor="fp-new" className="block text-xs font-bold text-gray-500 uppercase mb-1.5">New password</label>
                <div className="relative">
                  <input
                    id="fp-new"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className={`${inputCls} pr-11`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#7C3AED] transition"
                  >
                    {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="fp-confirm" className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Confirm password</label>
                <input
                  id="fp-confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className={inputCls}
                />
              </div>

              <button type="submit" disabled={loading} className={primaryBtn}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}

          <p className="text-center text-sm mt-6 text-gray-500">
            Remember your password?{' '}
            <Link to="/login" className="text-[#7C3AED] font-semibold hover:underline">
              Back to Login
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}

export default ForgotPassword;